import { format, parseISO, isValid } from "date-fns";

/**
 * Formats a time range for display.
 * Returns a formatted string like "Jan 1, 14:30 - Jan 2, 08:00"
 *
 * @param startTime - ISO date string for start time
 * @param endTime - ISO date string for end time
 * @param invalidMessage - Message to return if dates are invalid (for i18n)
 * @returns Formatted time range string or invalid message
 */
export function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  invalidMessage = "Invalid dates"
): string {
  if (!startTime || !endTime) {
    return invalidMessage;
  }

  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);

    if (!isValid(start) || !isValid(end)) {
      return invalidMessage;
    }

    return `${format(start, "MMM d, HH:mm")} - ${format(end, "MMM d, HH:mm")}`;
  } catch {
    return invalidMessage;
  }
}

/**
 * Formats a single time for display.
 * Returns a formatted string like "Jan 1, 14:30"
 *
 * @param time - ISO date string
 * @param invalidMessage - Message to return if date is invalid
 * @returns Formatted time string or invalid message
 */
export function formatSingleTime(
  time: string | null | undefined,
  invalidMessage = "Invalid date"
): string {
  if (!time) {
    return invalidMessage;
  }

  try {
    const date = parseISO(time);

    if (!isValid(date)) {
      return invalidMessage;
    }

    return format(date, "MMM d, HH:mm");
  } catch {
    return invalidMessage;
  }
}

/**
 * Calculates dwell time in hours between two ISO date strings.
 * Returns null if either date is invalid.
 *
 * @param startTime - ISO date string for start time
 * @param endTime - ISO date string for end time
 * @returns Dwell time in hours or null if invalid
 */
export function calculateDwellHours(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): number | null {
  if (!startTime || !endTime) {
    return null;
  }

  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);

    if (!isValid(start) || !isValid(end)) {
      return null;
    }

    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  } catch {
    return null;
  }
}
